import { clearConsent, setConsent } from '../../core/analytics/consent-store';
import { measurementId } from '../../core/analytics/google-analytics';
import { useCookieConsent } from '../../core/hooks/use-cookie-consent';
import { useTranslation } from '../../core/hooks/use-translation';

/**
 * The visitor's current cookie choice, and a way to change it, inside the section of the policy
 * that describes those cookies.
 *
 * **The right to withdraw consent is stated two paragraphs above this.** A policy that claims it and
 * then offers no way to exercise it except "clear your browser's site data" has not granted it. This
 * is where somebody looking for the control will look, which is why it is here rather than in a
 * footer link nobody reads.
 *
 * Withdrawing does not merely stop reporting: `stopAnalytics` also expires the cookies already set,
 * driven by the same store this writes to.
 */
export function CookiePreferences() {
    const { t } = useTranslation();
    const consent = useCookieConsent();

    // Nothing optional is loaded in a build with no property, so there is no choice to show.
    if (measurementId() === null) {
        return null;
    }

    return (
        <div className="info-box">
            <div className="info-box-icon">
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
            </div>
            <div>
                <p>
                    <strong>{t('cookies.prefs.title')}</strong>
                    <br />
                    {t(
                        consent === 'granted'
                            ? 'cookies.prefs.state_granted'
                            : consent === 'denied'
                              ? 'cookies.prefs.state_denied'
                              : 'cookies.prefs.state_undecided'
                    )}
                </p>

                <div className="cookie-prefs-actions">
                    {consent !== 'granted' && (
                        <button type="button" className="cookie-prefs-btn" onClick={() => setConsent('granted')}>
                            {t('cookies.accept')}
                        </button>
                    )}
                    {consent !== 'denied' && (
                        <button type="button" className="cookie-prefs-btn" onClick={() => setConsent('denied')}>
                            {/* Nothing has been given yet when the visitor is undecided, so there
                                is nothing to withdraw — the refusal is worded as the banner's. */}
                            {t(consent === 'granted' ? 'cookies.prefs.withdraw' : 'cookies.decline')}
                        </button>
                    )}
                    {consent !== null && (
                        <button type="button" className="cookie-prefs-btn" onClick={() => clearConsent()}>
                            {t('cookies.prefs.ask_again')}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
